"use client";

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PlusCircle, Clock, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import { useAuth } from "../../../contexts/AuthContext";
import { useConversationMessages } from "../../../hooks/use-conversations";
import { useResizablePanel } from "./hooks/useResizablePanel";
import { useStreamingChat } from "./hooks/useStreamingChat";
import { useInitialMessage } from "./hooks/useInitialMessage";
import { MessageList } from "../../ui/message-list";
import { MessageInput } from "../../ui/message-input";
import { WorkspacePanel } from "./components/WorkspacePanel";
import { ErrorDisplay } from "./components/ErrorDisplay";
import { useToast } from "../../ui/use-toast";
import type { ChatMessageProps } from "../../ui/chat-message";
import ConversationList from "../../Chat/ConversationList";
import type { UserMessage, AssistantMessage } from "./types";

// Add styles for markdown
import "./styles/markdown.css";

const AIChatPage = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const [activeTab, setActiveTab] = useState<string>("costs");
  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Session ID for tracking the conversation
  const sessionId = conversationId || "new";

  // Extract selectedProject from navigation state - memoized to prevent unnecessary re-computations
  const rawSelectedProject = useMemo(() => {
    if (
      location.state &&
      typeof location.state === "object" &&
      "selectedProject" in location.state
    ) {
      return (location.state as { selectedProject: string }).selectedProject;
    }
    return null;
  }, [location.state]);

  // Ensure we never propagate a NaN project id further down the chain
  const selectedProject = useMemo<string | null>(() => {
    if (!rawSelectedProject) return null;
    const n = Number(rawSelectedProject);
    return Number.isNaN(n) ? null : n.toString();
  }, [rawSelectedProject]);

  // projectId as number or null for history drawer
  const historyProjectId = useMemo(() => {
    if (!selectedProject) return null;
    const n = Number(selectedProject);
    return isNaN(n) ? null : n;
  }, [selectedProject]);

  // Panel resizing
  const {
    leftPanelWidth,
    isResizing,
    containerRef,
    resizeDividerRef,
    handleMouseDown,
  } = useResizablePanel();

  // Streaming chat functionality
  const {
    messages,
    isMessageSending,
    terraformFiles,
    costSummary,
    rightPanelState,
    resourceConfig,
    sendMessage,
    resetStreamingState,
    updateMessages,
  } = useStreamingChat({
    sessionId,
    token,
    selectedProject,
    onError: setError,
    onNavigate: navigate,
  });

  const isProcessing = isMessageSending;
  const [hasLoadedConversation, setHasLoadedConversation] = useState(false);

  // Handle initial message processing
  useInitialMessage({
    conversationId,
    sendMessage,
  });

  // Load conversation messages when navigating to an existing conversation
  const shouldLoadMessages = conversationId && conversationId !== "new";
  const { data: messagesData } = useConversationMessages(
    shouldLoadMessages ? conversationId : "",
    20 // pageSize
  );

  // Update messages when conversation data is loaded
  useEffect(() => {
    if (
      messagesData &&
      messagesData.pages &&
      messagesData.pages[0]?.messages &&
      !hasLoadedConversation &&
      conversationId !== "new"
    ) {
      const loadedMessages = messagesData.pages[0].messages.map((msg: any) => {
        if (msg.role === "user") {
          return {
            id: msg.id,
            type: "human",
            content: msg.content,
            timestamp: new Date(msg.timestamp).getTime(),
          } as UserMessage;
        } else {
          return {
            id: msg.id,
            backendId: msg.id,
            type: "assistant",
            content: msg.content,
            timestamp: new Date(msg.timestamp).getTime(),
            loading: false,
          } as AssistantMessage;
        }
      });

      // Only update if we haven't already loaded messages
      updateMessages(() => loadedMessages);
      setHasLoadedConversation(true);
    }
  }, [messagesData, hasLoadedConversation, conversationId, updateMessages]);

  // Reset loaded flag when conversation changes
  useEffect(() => {
    setHasLoadedConversation(false);
  }, [conversationId]);

  // Handle new chat navigation - memoized to prevent unnecessary re-renders
  const handleNewChat = useCallback(() => {
    resetStreamingState();
    setError(null);
    navigate("/chat-dashboard");
  }, [resetStreamingState, navigate]);

  // Handle message sending - memoized to prevent unnecessary re-renders
  const handleSendMessage = useCallback(
    (message: string, files?: File[]) => {
      setError(null);
      resetStreamingState();
      sendMessage(message, files);
    },
    [resetStreamingState, sendMessage]
  );

  // Handle message re-sending - memoized to prevent unnecessary re-renders
  const handleRedo = useCallback(
    (assistantUuid: string) => {
      const idx = messages.findIndex(
        (m: any) =>
          (m as any).backendId === assistantUuid || m.id === assistantUuid
      );
      if (idx === -1) return;
      // find previous human message
      const prevUser = [...messages]
        .slice(0, idx)
        .reverse()
        .find((m) => m.type === "human");

      if (!prevUser) return;

      // Reset the assistant message content and set loading true
      updateMessages((prev) =>
        prev.map((m) =>
          (m as any).backendId === assistantUuid || m.id === assistantUuid
            ? { ...m, content: "", loading: true }
            : m
        )
      );

      // Resend prompt without creating a new user message
      sendMessage(prevUser.content, undefined, { addUserMessage: false });
    },
    [messages, updateMessages, sendMessage]
  );

  // Determine waiting state
  const lastMessage = messages[messages.length - 1];
  const isWaiting =
    isMessageSending &&
    !(lastMessage?.type === "assistant" || lastMessage?.type === "token");

  // Map domain messages into chat SDK format and merge streaming token chunks
  const chatMessages = useMemo<ChatMessageProps[]>(() => {
    const aggregated: ChatMessageProps[] = [];

    const appendTokenToLast = (token: string) => {
      if (aggregated.length === 0) {
        aggregated.push({
          id: "stream",
          role: "assistant",
          content: token,
          createdAt: new Date(),
        });
        return;
      }
      const lastMsg = aggregated[aggregated.length - 1];
      if (lastMsg.role === "assistant") {
        lastMsg.content += token;
      } else {
        aggregated.push({
          id: "stream" + aggregated.length,
          role: "assistant",
          content: token,
          createdAt: new Date(),
        });
      }
    };

    messages.forEach((m) => {
      if (m.type === "token") {
        appendTokenToLast(m.content);
        return;
      }

      const msg: ChatMessageProps = {
        id: m.id,
        ...(m.type === "assistant" && (m as any).backendId
          ? { backendId: (m as any).backendId }
          : {}),
        role: m.type === "human" ? "user" : "assistant",
        content: m.content,
        createdAt: new Date(m.timestamp),
      };

      if (m.type === "assistant" && (m as any).toolCalls) {
        const toolCalls = (m as any).toolCalls as any[];
        msg.toolInvocations = toolCalls.map((tc) => ({
          state:
            tc.status === "running"
              ? "call"
              : tc.status === "error"
              ? "result"
              : "result",
          toolName: tc.toolName,
          ...(tc.status === "completed" && {
            result: { ...tc.args, details: tc.details },
          }),
          ...(tc.status === "error" && {
            result: { ...tc.args, error: tc.error },
          }),
        }));
      }

      aggregated.push(msg);
    });

    return aggregated;
  }, [messages]);

  // No custom messageOptions needed; MessageList handles feedback actions

  // Handle conversation selection from history
  const handleSelectConversation = useCallback(
    async (id: string) => {
      setIsLoadingHistory(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 100)); // Allow UI to update
        navigate(`/chat/${id}`, { state: { selectedProject } });
        setShowHistory(false);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [navigate, selectedProject]
  );

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden bg-[#FCFCFC]">
        <div className="flex-1 p-4 overflow-hidden">
          <div ref={containerRef} className="flex h-full overflow-hidden">
            {/* Chat Panel */}
            <div
              className="flex flex-col bg-white rounded-[20px] shadow-sm overflow-hidden border border-gray-200 relative"
              style={{ width: `${leftPanelWidth}%`, minWidth: "280px" }}
            >
              <div className="border-b px-6 py-4 flex items-center justify-between h-[60px] shrink-0">
                <h2 className="text-lg font-medium">Caden</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistory(!showHistory)}
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                    aria-label={showHistory ? "Close history" : "Open history"}
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={handleNewChat}
                    className="bg-green-500 hover:bg-black text-white"
                    size="sm"
                  >
                    <PlusCircle className="h-4 w-4 mr-1" />
                    New Chat
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-4">
                {isLoadingHistory ? (
                  <div className="flex h-full items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : showHistory ? (
                  <ConversationList
                    projectId={historyProjectId}
                    onSelect={handleSelectConversation}
                  />
                ) : (
                  <MessageList
                    messages={chatMessages}
                    conversationId={sessionId}
                    isTyping={isWaiting}
                    isGenerating={isProcessing}
                    onRedo={handleRedo}
                  />
                )}
              </div>

              <ErrorDisplay
                error={error}
                onDismiss={useCallback(() => setError(null), [])}
              />

              <div className="border-t p-3 shrink-0">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (input.trim()) {
                      handleSendMessage(input);
                      setInput("");
                    }
                  }}
                >
                  <MessageInput
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                    isGenerating={isProcessing}
                    allowAttachments={false}
                  />
                </form>
              </div>
            </div>

            {/* Resizable Divider */}
            <div
              ref={resizeDividerRef}
              className={`w-1 mx-2 cursor-col-resize flex items-center justify-center self-center h-[50px] rounded-[20px] hover:bg-gray-300 active:bg-gray-400 transition-colors ${
                isResizing ? "bg-gray-400" : "bg-gray-200"
              }`}
              onMouseDown={handleMouseDown}
            />

            {/* Workspace Panel */}
            <WorkspacePanel
              costSummary={costSummary}
              rightPanelState={rightPanelState}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              resourceConfig={resourceConfig}
              terraformFiles={terraformFiles}
              width={100 - leftPanelWidth - 1}
              isProcessing={isProcessing}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AIChatPage;
